import { Text, TextProps } from "@chakra-ui/react";
import { Dropdown, DropdownProps } from "components/dropdown";
import { PopoverProps } from "components/popover";
import { ErrorMessageLabel } from "./error-message-label";
import { useRef, useState, useEffect } from "react";

export interface DropdownFieldProps {
  title?: string;
  value?: any;
  options?: DropdownProps["options"];
  asyncOptions?: () => Promise<DropdownProps["options"]>;
  onChange?: DropdownProps["onChange"];
  isInvalid?: boolean;
  errorMessage?: string;
  dropdownProps?: DropdownProps;
  titleProps?: TextProps;
  popoverProps?: Omit<PopoverProps, "children" | "button">;
  messageErrorProps?: TextProps;
  onOpen?: (target: HTMLDivElement) => void;
  onClose?: (target: HTMLDivElement) => void;
  isDisabled?: boolean;
  isLoading?: boolean;
}

export function DropdownField({
  title,
  value,
  isInvalid,
  errorMessage,
  options,
  asyncOptions,
  onChange,
  titleProps,
  dropdownProps,
  popoverProps,
  messageErrorProps,
  onOpen,
  onClose,
  isDisabled,
  isLoading
}: DropdownFieldProps) {
  const errorBox = useRef<HTMLDivElement>(null);
  const [asyncLoadedOptions, setAsyncLoadedOptions] =
    useState<DropdownProps["options"]>(undefined);
  const [asyncIsLoading, setAsyncIsLoading] = useState(false);
  const { w, width, ...restDropdownProps } = dropdownProps || {};

  useEffect(() => {
    if (asyncOptions) {
      setAsyncIsLoading(true);
      asyncOptions()
        .then((loadedOptions) => {
          setAsyncLoadedOptions(loadedOptions);
          setAsyncIsLoading(false);
        })
        .catch((error) => {
          console.error("Erro ao carregar opções assíncronas:", error);
          setAsyncIsLoading(false);
        });
    }
  }, [asyncOptions]);

  const combinedOptions = asyncLoadedOptions || options;
  const combinedIsLoading = asyncIsLoading || isLoading;

  const onFocusIn = () => {
    if (errorMessage && errorBox.current) {
      errorBox.current.style.transform = `scale(0.8)`;
      errorBox.current.style.visibility = `hidden`;
      errorBox.current.style.opacity = `0`;
    }
  };
  const onFocusOut = () => {
    if (errorBox.current) {
      errorBox.current.style.transform = ``;
      errorBox.current.style.visibility = ``;
      errorBox.current.style.opacity = ``;
    }
  };

  return (
    <>
      {title ? (
        <Text mb="8px" fontWeight="medium" {...titleProps}>
          {title}
        </Text>
      ) : null}
      <ErrorMessageLabel
        ref={errorBox}
        errorMessage={errorMessage}
        messageErrorProps={messageErrorProps}
        containerProps={{ w: w || width || "100%" }}
      >
        <Dropdown
          options={combinedOptions}
          value={value}
          onChange={onChange}
          w={w || width || "100%"}
          isInvalid={isInvalid || !!errorMessage}
          onCloseEvent={(target) => {
            onFocusOut();
            onClose?.(target);
          }}
          onOpenEvent={(target) => {
            onFocusIn();
            onOpen?.(target);
          }}
          isDisabled={isDisabled}
          isLoading={combinedIsLoading}
          {...restDropdownProps}
        />
      </ErrorMessageLabel>
    </>
  );
}
